<template>
  <div class="colorPaletteInput">
    <Swatches
      v-if="swatches"
      @input="setColor"
      :value="value"
      :swatches="swatches"
      class="mb-2"
    />
    <div ref="picker" />
    <Input class="w-full" @change="setColor" :value="value" />
  </div>
</template>

<script>
import '@simonwep/pickr/dist/themes/monolith.min.css'; // 'monolith' theme
import debounce from 'lodash/debounce';

import Pickr from '@simonwep/pickr';
import Input from '../Input';
import Swatches from '../Swatches';

export default {
  name: 'ColorPaletteInput',

  components: {
    Swatches,
    Input,
  },
  props: {
    value: {
      type: String,
      default: 'hsla(60, 100%, 100%, 1)', // Don't set it to white
    },
    swatches: {
      type: Array,
      default() {
        return [];
      },
    },
  },

  data() {
    return {
      pickr: false,
      isClicking: false,
    };
  },

  mounted() {
    this.createPickr();
  },

  beforeDestroy() {
    this.destroyPickr();
  },

  watch: {
    value(val) {
      this.applyColor(val);
    },
  },

  methods: {
    change: debounce(function (color) {
      this.$emit('change', color);
    }, 150),
    onChange: function (color) {
      this.$emit('input', color);
      this.change(color);
    },
    createPickr() {
      /* We create a new el because the destroy function of the color picker doesn't return the DOM to the original state
			but simply deletes everything. 
			*/
      const $pickerEl = document.createElement('div');

      this.$refs.picker.appendChild($pickerEl);

      const pickr = new Pickr({
        // Selector or element which will be replaced with the actual color-picker.
        // Can be a HTMLElement.
        el: $pickerEl,

        // Where the pickr-app should be added as child.
        container: 'body',

        // Which theme you want to use. Can be 'monolith', 'monolith' or 'nano'
        theme: 'monolith',

        // Nested scrolling is currently not supported and as this would be really sophisticated to add this
        // it's easier to set this to true which will hide pickr if the user scrolls the area behind it.
        closeOnScroll: false,

        // Custom class which gets added to the pcr-app. Can be used to apply custom styles.
        appClass: 'custom-class',

        // Don't replace 'el' Element with the pickr-button, instead use 'el' as a button.
        // If true, appendToBody will also be automatically true.
        useAsButton: false,

        // Size of gap between pickr (widget) and the corresponding reference (button) in px
        padding: 8,

        // If true pickr won't be floating, and instead will append after the in el resolved element.
        // It's possible to hide it via .hide() anyway.
        inline: true,

        // If true, pickr will be repositioned automatically on page scroll or window resize.
        // Can be set to false to make custom positioning easier.
        autoReposition: false,

        // Start state. If true 'disabled' will be added to the button's classlist.
        disabled: false,

        // If true, the user won't be able to adjust any opacity.
        // Opacity will be locked at 1 and the opacity slider will be removed.
        // The HSVaColor object also doesn't contain an alpha, so the toString() methods just
        // print HSV, HSL, RGB, HEX, etc.
        lockOpacity: false,

        // Precision of output string (only effective if components.interaction.input is true)
        outputPrecision: 0,

        // If set to false it would directly apply the selected color on the button and preview.
        comparison: false,

        // Optional color swatches. When null, swatches are disabled.
        // Types are all those which can be produced by pickr e.g. hex(a), hsv(a), hsl(a), rgb(a), cmyk, and also CSS color names like 'magenta'.
        // Example: swatches: ['#F44336', '#E91E63', '#9C27B0', '#673AB7'],
        // swatches: ['#F44336', '#E91E63', '#9C27B0', '#673AB7'],
        default: this.value,

        // Default color representation of the input/output textbox.
        // Valid options are `HEX`, `RGBA`, `HSVA`, `HSLA` and `CMYK`.
        defaultRepresentation: 'HSLA',

        // Option to keep the color picker always visible.
        // You can still hide / show it via 'pickr.hide()' and 'pickr.show()'.
        // The save button keeps its functionality, so still fires the onSave event when clicked.
        showAlways: true,

        // Enables the ability to change numbers in an input field with the scroll-wheel.
        // To use it set the cursor on a position where a number is and scroll, use ctrl to make steps of five
        adjustableNumbers: true,

        // Show or hide specific components.
        // By default only the palette (and the save button) is visible.
        components: {
          // Defines if the palette itself should be visible.
          // Will be overwritten with true if preview, opacity or hue are true
          palette: true,

          preview: false, // Display comparison between previous state and new color
          opacity: true, // Display opacity slider
          hue: true, // Display hue slider

          // show or hide components on the bottom interaction bar.
          interaction: {
            // Buttons, if you disable one but use the format in default: or setColor() - set the representation-type too!
            hex: false, // Display 'input/output format as hex' button  (hexadecimal representation of the rgba value)
            rgba: false, // Display 'input/output format as rgba' button (red green blue and alpha)
            hsla: false, // Display 'input/output format as hsla' button (hue saturation lightness and alpha)
            hsva: false, // Display 'input/output format as hsva' button (hue saturation value and alpha)
            cmyk: false, // Display 'input/output format as cmyk' button (cyan mangenta yellow key )

            input: true, // Display input/output textbox which shows the selected color value.
            // the format of the input is determined by defaultRepresentation,
            // and can be changed by the user with the buttons set by hex, rgba, hsla, etc (above).
            cancel: false, // Display Cancel Button, resets the color to the previous state
            clear: false, // Display Clear Button; same as cancel, but keeps the window open
            save: false, // Display Save Button,
          },
        },

        // Button strings, brings the possibility to use a language other than English.
        strings: {
          save: 'Save', // Default for save button
          clear: 'Clear', // Default for clear button
          cancel: 'Cancel', // Default for cancel button
        },
      });

      this.pickr = pickr;

      pickr.on('change', (color, instance) => {
        this.onChange(color.toHSLA().toString(2));
      });
    },

    /* Sets color silently */
    applyColor(val) {
      if (this.pickr && val != this.pickr.getColor().toHSLA()) {
        this.pickr.setColor(val, true);
      }
    },

    setColor(val) {
      if (this.pickr && val != this.pickr.getColor().toHSLA()) {
        this.pickr.setColor(val);
      }
    },

    destroyPickr() {
      if (this.pickr && this.pickr.destroyAndRemove) {
        this.pickr.destroyAndRemove();
      }
    },
  },
};
</script>

<style>
/* Overwrite some default Pickr app styling */
.pcr-app {
  box-shadow: none !important;
  padding: 0px !important;
}

.pickr {
  display: none;
}

.pcr-picker {
  width: 14px !important;
  height: 14px !important;
}

/* Make picker responsive.... */
.pcr-color-palette:after {
  padding-bottom: 100%;
  content: '';
}

.pcr-app .pcr-selection .pcr-picker {
  z-index: 1;
}

.pcr-app[data-theme='monolith'] .pcr-selection .pcr-color-palette {
  height: auto !important;
}

.pcr-app[data-theme='monolith'] .pcr-selection .pcr-color-palette {
  width: 100% !important;
}

.pcr-app[data-theme='monolith'] .pcr-selection .pcr-color-palette .pcr-palette {
  flex-grow: 1;
  border-radius: 0.15em;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.pcr-app[data-theme='monolith'] {
  width: 100% !important;
}

.pcr-interaction {
  display: none !important;
}
</style>
